// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//


import { GetPagesByParentApiParameter } from "@awc/App/Services/GetAlliedUniversityChildrenService";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import { getPagesByParent, PagesCollectionModel } from "@insite/client-framework/Services/ContentService";


export interface PageByParentApiResult {
    result?: PagesCollectionModel;
    successful?: boolean;
    errorMessage?: string;
}

type HandlerType = ApiHandler<
    GetPagesByParentApiParameter & HasOnSuccess<PagesCollectionModel> & HasOnError<string>,
    PageByParentApiResult
>;
export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
    (props as any).key = props.apiParameter.parentId;
};

export const DispatchBeginGetPagesContent: HandlerType = props => {
    props.dispatch({
        type: "xPagesContent/BeginLoadPagesContent",
        key: (props as any).key,
    } as any);
};

export const GetPagesContent: HandlerType = async props => {
    const { onSuccess, onError, ...apiParameter } = props.apiParameter;

    try {
        const result = await getPagesByParent({ parentNodeId: apiParameter.parentId });
        props.apiResult = {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            props.apiResult = {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
};

export const DispatchCompleteGetPagesContent: HandlerType = props => {
    props.dispatch({
        type: "xPagesContent/CompleteLoadPagesContent",
        data: (props.apiResult as any)?.result?.pages,
        key: (props as any).key,
        isMainRoot: false,
        state: props.getState(),
    } as any);
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        props.dispatch({
            type: "xPagesContent/FailedToLoadPagesContent",
            key: (props as any).key,
        } as any);
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult.result!);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginGetPagesContent,
    GetPagesContent,
    DispatchCompleteGetPagesContent,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];
const getPagesByParentId = createHandlerChainRunner(chain, "GetPagesByParentId");

export default getPagesByParentId;
